import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpService } from '../../store/http/http.service';
import { v4 as uuidv4 } from 'uuid';

type CreateMultipartResponse = {
  uploadId: string;
  key: string; // S3 object key
  bucket?: string; // optional
};

type GetPartUrlsResponse = {
  urls: Record<number, string>; // partNumber -> presignedUrl
};

type CompleteMultipartPayload = {
  uploadId: string;
  key: string;
  parts: { PartNumber: number; ETag: string }[];
};

type AbortMultipartPayload = {
  uploadId: string;
  key: string;
};

export interface PresignResponse {
  url: string;
  thumbnailUrl?: string;
}

export interface FilePreview {
  // UI-only, not persisted
  /** For immediate thumbnail/video preview: blob:... */
  objectUrl: string;

  /** Optional: if you need inline base64 thumbnails */
  dataUrl?: string;
}

// Shared helpers
export type FileKind = 'image' | 'video' | 'audio' | 'doc' | 'other';

export interface FileMetaData {
  id: string;
  url?: string;
  type: FileKind;
  fileName?: string;
  extension?: string;
  sizeInMb?: number;
  mimeType?: string;
}

export interface MediaMetaData extends FileMetaData {
  width?: number;
  height?: number;
}

export interface VideoMetaData extends MediaMetaData {
  type: 'video';
  duration?: number; // seconds
}

export interface SoundFileMetaData extends FileMetaData {
  type: 'audio';
  duration?: number; // seconds
}

export interface ImageFileMetaData extends MediaMetaData {
  type: 'image';
}

export interface DocFileMetaData extends FileMetaData {
  type: 'doc';
}

export interface ImageUploadResult {
  id: string;
  dataUrl: string;
  width: number;
  height: number;
  extension: string;
  sizeInMb: number;
}

export interface VideoUploadResult {
  id: string;
  objectUrl: string;
  thumbnailDataUrl: string;
  width: number;
  height: number;
  duration: number;
  extension: string;
  sizeInMb: number;
  fileName: string;
}

export interface ProcessedFile {
  meta: FileMetaData;
  preview?: FilePreview;
  /** Keep original file for upload step */
  file: File;
}

export interface ProcessOptions {
  preview?: 'none' | 'objectUrl' | 'dataUrl';
}

@Injectable({ providedIn: 'root' })
export class FileService {
  private static readonly CDN_BASE = 'https://private.cdn.inwedio.com';
  private uploadProgress = new Map<string, BehaviorSubject<number>>();

  constructor(private readonly httpService: HttpService) {
    console.log('file-service > constructor', this.httpService);
  }

  /**
   * Resolve a displayable URL for a file based on its metadata.
   * Uses the CDN pattern: images/{id} or videos/{id}/video.{ext}
   */
  getFileUrl(meta: FileMetaData): string | undefined {
    if (meta.url) return meta.url;
    if (!meta.id) return undefined;

    switch (meta.type) {
      case 'image':
        return `${FileService.CDN_BASE}/images/${meta.id}`;
      case 'video':
        return meta.extension
          ? `${FileService.CDN_BASE}/videos/${meta.id}/video.${meta.extension}`
          : `${FileService.CDN_BASE}/videos/${meta.id}/video.mp4`;
      default:
        return undefined;
    }
  }

  getUploadProgress(fileId: string): Observable<number> | null {
    return this.uploadProgress.get(fileId)?.asObservable() ?? null;
  }

  private startProgress(fileId: string): BehaviorSubject<number> {
    const subject = new BehaviorSubject<number>(0);
    this.uploadProgress.set(fileId, subject);
    return subject;
  }

  private completeProgress(fileId: string): void {
    const subject = this.uploadProgress.get(fileId);
    if (subject) {
      subject.next(100);
      subject.complete();
    }
  }

  private failProgress(fileId: string): void {
    const subject = this.uploadProgress.get(fileId);
    if (subject) {
      subject.error('Upload failed');
      this.uploadProgress.delete(fileId);
    }
  }

  async processFile(
    file: globalThis.File,
    opts: ProcessOptions = { preview: 'objectUrl' },
  ): Promise<ProcessedFile> {
    const mimeType = file.type || '';
    const fileName = file.name || '';
    const sizeInBytes = file.size;

    const extension = (() => {
      const i = fileName.lastIndexOf('.');
      return i > 0 ? fileName.slice(i + 1).toLowerCase() : '';
    })();

    const id = crypto.randomUUID();

    const type: FileKind = mimeType.startsWith('image/')
      ? 'image'
      : mimeType.startsWith('video/')
        ? 'video'
        : mimeType.startsWith('audio/')
          ? 'audio'
          : mimeType === 'application/pdf' ||
              mimeType.startsWith('application/msword') ||
              mimeType.startsWith('application/vnd')
            ? 'doc'
            : 'other';

    const objectUrl = URL.createObjectURL(file);

    try {
      const meta: FileMetaData = {
        id,
        type,
        fileName,
        extension,
        sizeInMb: sizeInBytes / (1024 * 1024),
        mimeType,
      };

      // metadata extraction
      if (type === 'image') {
        const { width, height } = await this.getImageDimensions(objectUrl);
        (meta as ImageFileMetaData).width = width;
        (meta as ImageFileMetaData).height = height;
      } else if (type === 'video') {
        const { width, height, duration } =
          await this.getVideoMetadata(objectUrl);
        (meta as VideoMetaData).width = width;
        (meta as VideoMetaData).height = height;
        (meta as VideoMetaData).duration = duration;
      } else if (type === 'audio') {
        const { duration } = await this.getAudioMetadata(objectUrl);
        (meta as SoundFileMetaData).duration = duration;
      }

      // preview generation
      let preview: FilePreview | undefined;
      if (opts.preview === 'objectUrl') {
        preview = { objectUrl };
        // IMPORTANT: do NOT revoke yet — UI is using it.
        return { meta, preview, file };
      }

      if (opts.preview === 'dataUrl') {
        const dataUrl = await this.readAsDataURL(file);
        preview = { objectUrl, dataUrl };
        return { meta, preview, file };
      }

      // preview none → safe to revoke immediately
      URL.revokeObjectURL(objectUrl);
      return { meta, file };
    } catch (e) {
      console.error('Failed to process file', e);
      URL.revokeObjectURL(objectUrl);
      throw e;
    }
  }

  readAsDataURL(file: globalThis.File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () =>
        reject(new Error('Failed to read file as DataURL'));
      reader.readAsDataURL(file);
    });
  }
  /** Helpers */

  getImageDimensions(url: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () =>
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error('Failed to load image metadata'));
      img.src = url;
    });
  }

  getVideoMetadata(
    url: string,
  ): Promise<{ width?: number; height?: number; duration?: number }> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true; // helps some browsers
      video.onloadedmetadata = () => {
        const duration = Number.isFinite(video.duration)
          ? video.duration
          : undefined;
        resolve({
          width: video.videoWidth || undefined,
          height: video.videoHeight || undefined,
          duration,
        });
        // no need to play; metadata is enough
      };
      video.onerror = e => {
        console.error('Video error', e, video.error);
        reject(new Error('Failed to load video metadata'));
      };
      video.src = url;
    });
  }

  getAudioMetadata(url: string): Promise<{ duration?: number }> {
    return new Promise((resolve, reject) => {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';
      audio.onloadedmetadata = () => {
        const duration = Number.isFinite(audio.duration)
          ? audio.duration
          : undefined;
        resolve({ duration });
      };
      audio.onerror = () => reject(new Error('Failed to load audio metadata'));
      audio.src = url;
    });
  }

  async uploadFles(files: ProcessedFile[]) {
    await Promise.all(files.map(file => this.uploadFile(file)));
  }

  async uploadFile(processedFile: ProcessedFile) {
    const { meta } = processedFile;
    const { type, id } = meta;
    const progress = this.startProgress(id);

    try {
      if (type === 'image') {
        await this.uploadImageFile(processedFile, p => progress.next(Math.round(p * 100)));
      } else if (type === 'video') {
        await this.uploadVideoFile(processedFile, p => progress.next(Math.round(p * 100)));
      }
      this.completeProgress(id);
    } catch (err) {
      this.failProgress(id);
      throw err;
    }
  }

  // async uploadAudioFile(file: File, id: string): Promise<SoundFileMetaData> {
  //   const extension = file.name.split('.').pop() || '';
  // }

  /**
   * Upload an image file to S3 using presigned URL
   */
  async uploadImageFile(processedFile: ProcessedFile, onProgress?: (p: number) => void): Promise<void> {
    const { meta, file } = processedFile;
    const presign = await this.getPresignedPutUrl(meta.id, 'image');
    await this.uploadToS3WithProgress(presign.url, file, onProgress);
  }

  /**
   * Upload a video file to S3 using presigned URL
   */
  /**
   * Get presigned PUT URL from backend
   */
  private async getPresignedPutUrl(
    uuid: string,
    type: 'image' | 'video',
  ): Promise<PresignResponse> {
    const { data } = await this.httpService._postPromise<PresignResponse>(
      'file/presign-put',
      { uuid, type },
    );
    return data;
  }

  /**
   * Upload file to S3 using presigned URL
   */
  private async uploadToS3(
    presignedUrl: string,
    file: File | Blob,
  ): Promise<void> {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(
        `S3 PUT failed: ${response.status} ${response.statusText}`,
      );
    }
  }

  /**
   * Upload file to S3 with progress reporting via XHR
   */
  private uploadToS3WithProgress(
    presignedUrl: string,
    file: File | Blob,
    onProgress?: (p: number) => void,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

      if (onProgress) {
        xhr.upload.addEventListener('progress', e => {
          if (e.lengthComputable) {
            onProgress(e.loaded / e.total);
          }
        });
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`S3 PUT failed: ${xhr.status} ${xhr.statusText}`));
        }
      };
      xhr.onerror = () => reject(new Error('S3 PUT network error'));
      xhr.send(file);
    });
  }

  /**
   * Read file as data URL
   */
  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Load video and get metadata
   */
  private loadVideoMetadata(objectUrl: string): Promise<HTMLVideoElement> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      video.onloadedmetadata = () => resolve(video);
      video.onerror = () => reject(new Error('Failed to load video metadata'));

      video.src = objectUrl;
      video.load();
    });
  }

  /**
   * Get first frame thumbnail from video element
   */
  private async getFirstFrameThumbnail(
    videoEl: HTMLVideoElement,
    opts?: {
      seekTo?: number;
      maxWidth?: number;
      mimeType?: string;
      quality?: number;
    },
  ): Promise<string> {
    const seekTo = opts?.seekTo ?? 0.1;
    const maxWidth = opts?.maxWidth ?? 480;
    const mimeType = opts?.mimeType ?? 'image/jpeg';
    const quality = opts?.quality ?? 0.82;

    const targetTime = Math.min(
      Math.max(seekTo, 0),
      Math.max(videoEl.duration - 0.01, 0),
    );

    await new Promise<void>((resolve, reject) => {
      const onSeeked = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error('Video seek error'));
      };
      const cleanup = () => {
        videoEl.removeEventListener('seeked', onSeeked);
        videoEl.removeEventListener('error', onError);
      };

      videoEl.addEventListener('seeked', onSeeked, { once: true });
      videoEl.addEventListener('error', onError, { once: true });
      videoEl.currentTime = targetTime;
    });

    const srcW = videoEl.videoWidth;
    const srcH = videoEl.videoHeight;
    const scale = Math.min(1, maxWidth / srcW);
    const outW = Math.round(srcW * scale);
    const outH = Math.round(srcH * scale);

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas 2d context');

    ctx.drawImage(videoEl, 0, 0, outW, outH);
    return canvas.toDataURL(mimeType, quality);
  }

  thumbnailCanvas: HTMLCanvasElement;

  async saveThumbnailOfVideo(
    videoEl: HTMLVideoElement,
    opts?: {
      seekTo?: number;
      maxWidth?: number;
      mimeType?: string;
      quality?: number;
      videoId: string;
      thumbnailId: string;
    },
  ) {
    const maxWidth = opts?.maxWidth ?? 480;
    const mimeType = opts?.mimeType ?? 'image/jpeg';
    const quality = opts?.quality ?? 0.82;

    const srcW = videoEl.videoWidth;
    const srcH = videoEl.videoHeight;
    const scale = Math.min(1, maxWidth / srcW);
    const outW = Math.round(srcW * scale);
    const outH = Math.round(srcH * scale);

    this.thumbnailCanvas ||= document.createElement('canvas');
    this.thumbnailCanvas.width = outW;
    this.thumbnailCanvas.height = outH;

    const ctx = this.thumbnailCanvas.getContext('2d');
    if (!ctx) throw new Error('Could not get this.thumbnailCanvas 2d context');

    ctx.drawImage(videoEl, 0, 0, outW, outH);

    const blob = await new Promise<Blob>(resolve => {
      this.thumbnailCanvas.toBlob(
        blob => {
          resolve(blob);
        },
        mimeType,
        1,
      );
    });
    const { videoId, thumbnailId } = opts;
    this.uploadBlobToS3(videoId, thumbnailId, blob);
  }

  async uploadBlobToS3(videoId: string, thumbnailId: string, file: Blob) {
    const presign = await this.getPresignedPutUrl(
      `${videoId}/thumbnail-${thumbnailId}.jpg`,
      'video',
    );
    await this.uploadToS3(presign.url, file);
  }

  /**
   * Convert data URL to Blob
   */
  private dataUrlToBlob(dataUrl: string): Blob {
    const [header, base64] = dataUrl.split(',');
    const mimeMatch = header.match(/data:(.*?);base64/);
    const mime = mimeMatch?.[1] ?? 'image/jpeg';

    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);

    return new Blob([bytes], { type: mime });
  }

  async uploadVideoFile(
    processedFile: ProcessedFile,
    onProgress?: (p: number) => void,
  ): Promise<VideoUploadResult> {
    const { meta, file, preview } = processedFile;
    const { id } = meta;

    const extension = file.name.split('.').pop() || '';
    const objectUrl = preview?.objectUrl;

    const partSizeBytes = 16 * 1024 * 1024; // 16MB

    // Needed for abort in catch
    let uploadId: string | undefined;
    let key: string | undefined;

    try {
      const videoEl = await this.loadVideoMetadata(objectUrl);

      const thumbnailDataUrl = await this.getFirstFrameThumbnail(videoEl);
      const thumbnailBlob = this.dataUrlToBlob(thumbnailDataUrl);
      const thumbnailFile = new File([thumbnailBlob], `${id}.jpg`, {
        type: 'image/jpeg',
      });

      // 1) INIT
      const init = await this.httpService.createVideoMultipartUpload(
        id,
        file.type,
      );
      uploadId = init.uploadId;
      key = init.key;

      // 2) Get ALL part URLs (one request)
      const totalParts = Math.ceil(file.size / partSizeBytes);

      // If totalParts can be big, this array might be large—still OK up to a point.
      const partNumbers = Array.from({ length: totalParts }, (_, i) => i + 1);

      const { urls } = await this.httpService.getVideoMultipartPartUrls({
        id,
        uploadId,
        key,
        partNumbers,
      });

      // 3) Upload parts to S3 (no server calls inside)
      const { parts } = await this.uploadToS3Multipart({
        file,
        contentType: file.type || 'application/octet-stream',
        uploadId,
        key,
        partUrls: urls,
        partSizeBytes,
        onProgress,
      });

      this.uploadThumbnailToS3(id, thumbnailFile);
      // 4) COMPLETE
      await this.httpService.completeVideoMultipartUpload({
        id,
        uploadId,
        key,
        parts,
      });

      // Thumbnail upload
      // const thumbPresign = await this.getPresignedPutUrl(id, 'thumbnail');
      // await this.uploadToS3(thumbPresign.url, thumbnailFile);

      return {
        id,
        objectUrl,
        thumbnailDataUrl,
        width: videoEl.videoWidth,
        height: videoEl.videoHeight,
        duration: videoEl.duration,
        extension,
        sizeInMb: file.size / (1024 * 1024),
        fileName: file.name,
      };
    } catch (error) {
      if (uploadId && key) {
        try {
          await this.httpService.abortVideoMultipartUpload({
            id,
            uploadId,
            key,
          });
        } catch {
          // ignore abort errors
        }
      }
      URL.revokeObjectURL(objectUrl);
      throw error;
    }
  }

  private async uploadThumbnailToS3(
    id: string,
    thumbnailFile: File,
  ): Promise<void> {
    const presign = await this.getPresignedPutUrl(
      `${id}/thumbnail.jpg`,
      'video',
    );
    await this.uploadToS3(presign.url, thumbnailFile);
  }

  private async uploadToS3Multipart(params: {
    file: File | Blob;
    contentType: string;
    uploadId: string; // not used here, but you might keep for logging/debug
    key: string; // not used here, but you might keep for logging/debug
    partUrls: Record<number, string>;
    partSizeBytes: number;
    onProgress?: (p: number) => void;
  }): Promise<{ parts: { PartNumber: number; ETag: string }[] }> {
    const { file, contentType, partUrls, partSizeBytes, onProgress } = params;

    const totalSize = file.size;
    const totalParts = Math.ceil(totalSize / partSizeBytes);

    const parts: { PartNumber: number; ETag: string }[] = [];
    let uploadedBytes = 0;

    const promises: { promise: Promise<Response>; partNumber: number }[] = [];
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const start = (partNumber - 1) * partSizeBytes;
      const end = Math.min(start + partSizeBytes, totalSize);
      const chunk = (file as Blob).slice(start, end);

      const presignedUrl = partUrls[partNumber];
      if (!presignedUrl)
        throw new Error(`Missing presigned URL for part ${partNumber}`);

      const res = new Promise<Response>((resolve, reject) => {
        fetch(presignedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': contentType || 'application/octet-stream',
          },
          body: chunk,
        }).then(res => {
          if (!res.ok) {
            throw new Error(
              `S3 UploadPart failed (part ${partNumber}): ${res.status} ${res.statusText}`,
            );
          }
          const etagRaw = res.headers.get('ETag');
          if (!etagRaw)
            throw new Error(`Missing ETag header for part ${partNumber}`);

          parts.push({
            PartNumber: partNumber,
            ETag: etagRaw.replace(/^"|"$/g, ''),
          });
          uploadedBytes += chunk.size;
          onProgress?.(uploadedBytes / totalSize);
          resolve(res);
        });
      });

      promises.push({ promise: res, partNumber });
    }

    await Promise.all(promises.map(p => p.promise));

    parts.sort((a, b) => a.PartNumber - b.PartNumber);
    return { parts };
  }
}
